import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { warmupDisplayDay } from '../../../music24/outreach/live/warmupDay';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { mailboxMessages as messages } from './mailboxMessages';
import { rampCapsByDay, rampEnds } from './mailboxLines';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Mailboxes card; each part uses its own slice of it */
import styles from './Mailboxes.module.scss';

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

interface WarmupChartProps {
  mailbox: Pick<MailboxRow, 'warmup' | 'sentToday' | 'capToday'>;
}

/**
 * The warm-up of a mailbox as one chart (design outreach-v2 q1): one bar per day from the connect
 * day to the end of the warm-up, its height is the cap of that day (`OUTREACH_RAMP`). The days that
 * passed are pale, today is solid violet. Under it: the first cap, the next cap and the last cap.
 */
const WarmupChartComponent: React.FC<WarmupChartProps> = ({ mailbox }) => {
  const intl = useIntl();
  const { warmup } = mailbox;
  const { first, max } = rampEnds();
  const caps = useMemo(() => rampCapsByDay(warmup.totalDays), [warmup.totalDays]);
  const bars = useMemo(
    () =>
      caps.map((cap, day) => (
        <i
          // eslint-disable-next-line react/no-array-index-key -- one bar per day, the day is the key
          key={day}
          className={clsx(day < warmup.day && styles.barPast, day === warmup.day && styles.barNow)}
          // The height is data: the cap of the day against the last cap.
          style={{ height: `${Math.max(10, Math.round((cap / Math.max(1, max)) * 100))}%` }}
        />
      )),
    [caps, max, warmup.day]
  );
  return (
    <div className={styles.meterBlock} data-testid="mailbox-warmup">
      <div className={styles.meterTop}>
        <span>
          {intl.formatMessage(messages.warmupDay, {
            day: warmupDisplayDay(warmup),
            total: warmup.totalDays,
            b: bold,
          })}
        </span>
        <span>
          {intl.formatMessage(messages.sentToday, { sent: mailbox.sentToday, cap: mailbox.capToday, b: bold })}
        </span>
      </div>
      <div
        className={styles.bars}
        role="img"
        aria-label={intl.formatMessage(messages.chartLabel, { cap: mailbox.capToday, max })}
      >
        {bars}
      </div>
      <div className={styles.meterFoot}>
        <span>{intl.formatMessage(messages.perDayCap, { cap: first })}</span>
        {warmup.nextCap !== null && warmup.nextCapAt !== null && (
          <span data-testid="mailbox-next-cap">
            {intl.formatMessage(messages.nextCap, {
              cap: warmup.nextCap,
              date: intl.formatDate(new Date(warmup.nextCapAt), { day: 'numeric', month: 'short' }),
              b: bold,
            })}
          </span>
        )}
        <span>{intl.formatMessage(messages.perDayCap, { cap: max })}</span>
      </div>
    </div>
  );
};

export const WarmupChart = TypedMemo(WarmupChartComponent);
